import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concatMap,
  filter,
  from,
  map,
  mergeMap,
  MonoTypeOperatorFunction,
  Observable,
  of,
  toArray,
} from 'rxjs';
import { Option, Result } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { Survey } from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  SurveyPersistenceService,
  SurveyUpdateRequest,
} from '../../services/save/survey-persistence.service';
import { SurveySaveInputBuilder } from '../../services/save/survey-save-input.builder';
import { selectAudienceChildren } from '../audience/survey-creation-audience.selectors';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyCreationCoreApiActions } from './survey-creation-core.actions';
import {
  selectActiveChildKey,
  selectCoreChildren,
  selectSurveyFor,
} from './survey-creation-core.selectors';
import {
  firstFormError,
  toValidatedSurvey,
} from './utils/survey-save-validation.helper';
import { resolveSaveSurveyForKeys } from './utils/survey-save-for.helper';
import {
  DEFAULT_SAVE_GATE,
  traditionalMcLocaleMessage,
} from './utils/survey-save-pipeline.helper';
import {
  gatherSurveyFormContextFor,
  SurveyFormContext,
} from './utils/survey-form-context.helper';

// A Traditional MC locale is its own survey: once the active one saves, every
// participating locale with unsaved targeting is saved after it, one by one.
@Injectable()
export class SurveyCreationUnsavedMcChildSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _toastService = inject(ToastService);
  private readonly _persistence = inject(SurveyPersistenceService);
  private readonly _input = inject(SurveySaveInputBuilder);

  public readonly saveUnsavedChildren$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.saveSurveySuccess),
      concatLatestFrom(() => [
        this._store.select(selectActiveChildKey),
        this._store.select(selectCoreChildren),
        this._store.select(selectAudienceChildren),
      ]),
      concatMap(([action, activeChildKey, coreChildren, audienceChildren]) =>
        from(
          resolveSaveSurveyForKeys(
            action.survey,
            activeChildKey,
            coreChildren,
            audienceChildren,
          ).filter((key) => String(key) !== String(activeChildKey)),
        ).pipe(
          concatMap((key) => this._saveChild(action, key)),
          toArray(),
          mergeMap((results) => [
            ...results,
            SurveyCreationCoreApiActions.saveSiblingSurveysComplete({
              allSaved: results.every(
                ({ type }) =>
                  type ===
                  SurveyCreationCoreApiActions.saveSiblingSurveySuccess.type,
              ),
            }),
          ]),
        ),
      ),
    ),
  );

  private _saveChild(trigger: Action, key: SurveyChildKey): Observable<Action> {
    return of(trigger).pipe(
      gatherSurveyFormContextFor(this._store, key),
      concatLatestFrom(() => this._store.select(selectSurveyFor(key))),
      concatMap(([context, survey]) =>
        this._request(context, survey).match({
          ok: (request) => this._write(key, survey, request),
          err: (error) => of(this._failure(key, survey, error)),
        }),
      ),
      this._whileRegistered(key),
    );
  }

  // The child's own state, through the same gate and builder as the active
  // save; its launch form only gates once edited, and no overrides apply.
  private _request(
    context: SurveyFormContext,
    survey: Nullable<Survey>,
  ): Result<SurveyUpdateRequest, string> {
    return toValidatedSurvey(context, DEFAULT_SAVE_GATE, {})
      .okOr(firstFormError(context))
      .andThen((validated) => this._input.request(validated, survey, {}));
  }

  private _write(
    key: SurveyChildKey,
    survey: Nullable<Survey>,
    request: SurveyUpdateRequest,
  ): Observable<Action> {
    return this._persistence.update(request).pipe(
      map((savedChild) =>
        SurveyCreationCoreApiActions.saveSiblingSurveySuccess({
          survey: savedChild,
        }),
      ),
      this.psCatchError(
        (error) => of(this._failure(key, survey, error.message)),
        { notify: false },
      ),
    );
  }

  private _failure(
    key: SurveyChildKey,
    survey: Nullable<Survey>,
    error: string,
  ): Action {
    const message = Option.from(survey).mapOr(error, (located) =>
      traditionalMcLocaleMessage(error, located),
    );
    this._toastService.error(message);
    return SurveyCreationCoreApiActions.saveSiblingSurveyFailure({
      key,
      error: message,
    });
  }

  // Leaving the workflow resets the registry; a late result is dropped rather
  // than patched into whatever survey loads next.
  private _whileRegistered(
    key: SurveyChildKey,
  ): MonoTypeOperatorFunction<Action> {
    return (action$) =>
      action$.pipe(
        concatLatestFrom(() => this._store.select(selectCoreChildren)),
        filter(([, children]) => !!children[key]),
        map(([action]) => action),
      );
  }
}
